import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ShieldCheck } from 'lucide-react'
import Dialog from '../ui/dialog'
import Button from '../ui/button'
import { track } from '../../lib/analytics/posthog'
import { startCheckoutSession } from '../../lib/start-checkout-session'
import { PLANS, getMinPlanForPaywall, type PlanId } from '../../lib/plans'

export interface UpgradeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  bullets: string[]
  paywallId: string
  // Optional override for the cheapest plan that unlocks this feature. When omitted,
  // the dialog derives it from `paywallId` so the "From" price always matches what the
  // user can actually buy to unlock the feature.
  minPlanId?: PlanId
  // Optional callback when user proceeds to billing (before navigation)
  onProceedToBilling?: () => void
}

const UpgradeDialog: React.FC<UpgradeDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  bullets,
  paywallId,
  minPlanId,
  onProceedToBilling,
}) => {
  const minPlan = PLANS[minPlanId ?? getMinPlanForPaywall(paywallId)]
  const navigate = useNavigate()
  const [hasSeenBilling, setHasSeenBilling] = React.useState(false)

  React.useEffect(() => {
    setHasSeenBilling(localStorage.getItem('has_seen_billing') === 'true')
  }, [])

  React.useEffect(() => {
    if (open) {
      track('paywall_dialog_shown', {
        paywall_id: paywallId,
        title,
      })
    }
  }, [open, paywallId, title])

  const handleViewPlans = () => {
    track('paywall_dialog_action', { paywall_id: paywallId, action: 'view_plans' })
    // Allow parent to persist any transient state before navigating away
    try { onProceedToBilling?.() } catch {}
    onOpenChange(false)
    navigate(`/billing?from=${paywallId}`)
  }
  const handleNotNow = () => {
    track('paywall_dialog_action', { paywall_id: paywallId, action: 'dismiss' })
    onOpenChange(false)
  }

  const handleQuickStart = async () => {
    track('paywall_dialog_action', { paywall_id: paywallId, action: 'quick_start_checkout' })
    try { onProceedToBilling?.() } catch {}
    await startCheckoutSession(minPlan.id)
  }

  const footer = (
    <div className="flex flex-col w-full gap-3 px-4 sm:px-6 py-4">
      <div className="flex flex-wrap justify-end items-center gap-3">
        <div className="flex flex-col items-start mr-auto">
          <span className="text-[10px] uppercase tracking-wider font-bold text-foreground/40">From {minPlan.priceLabel}/mo</span>
          <div className="flex items-center gap-1 text-[10px] text-success-600 font-medium">
            <ShieldCheck className="w-3 h-3" />
            7-day money-back guarantee
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            type="button" 
            variant="ghost" 
            onClick={handleNotNow}
          >
            Maybe later
          </Button>

          {hasSeenBilling ? (
            <>
              <Button 
                type="button" 
                variant="secondary"
                onClick={handleViewPlans}
                className="hidden sm:inline-flex"
              >
                View all plans
              </Button>
              <Button 
                type="button" 
                onClick={handleQuickStart}
              >
                Get {minPlan.name.toLowerCase()}
              </Button>
            </>
          ) : (
            <Button 
              type="button" 
              onClick={handleViewPlans}
            >
              View plans
            </Button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="lg"
      heightMode="auto"
      footer={footer}
    >
      <div className="space-y-4">
        <ul className="grid grid-cols-1 gap-3">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 group">
              <div className="mt-0.5 w-5 h-5 rounded-full bg-success-500/10 flex items-center justify-center text-success-600 shrink-0 group-hover:bg-success-500/20 transition-colors">
                <Check className="w-3 h-3 stroke-[3]" />
              </div>
              <span className="text-sm text-foreground/80 font-medium">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  )
}

export default UpgradeDialog
